import { TAppState } from '../../redux/types';

import { collectLayout } from './editorLayout.collect';
import { getRestoredProjectId } from './editorLayout.gate';
import { saveLayout } from './editorLayout.storage';
import { TEditorLayout } from './editorLayout.types';

const WRITE_THROTTLE_MS = 500;

type TLayoutStore = {
  getState(): TAppState;
  subscribe(listener: () => void): () => void;
};

// Ровно те ветки состояния, из которых собирается срез. Сравниваются по ссылке —
// шесть сравнений на экшен, дешевле любой сериализации.
type TWatched = {
  project: TAppState['editor']['project'];
  selected: TAppState['editor']['selected'];
  secondarySelected: TAppState['editor']['secondarySelected'];
  isCompiled: boolean;
  isSecondaryCompiled: boolean;
  contentWindows: TAppState['contentWindows'];
};

function watched(state: TAppState): TWatched {
  return {
    project: state.editor.project,
    // editor.selected обязан быть здесь: переход на другую заметку меняет ТОЛЬКО
    // это поле, и без него сохранённая заметка не обновлялась бы никогда.
    selected: state.editor.selected,
    secondarySelected: state.editor.secondarySelected,
    isCompiled: state.general.isCompiled,
    isSecondaryCompiled: state.general.isSecondaryCompiled,
    contentWindows: state.contentWindows,
  };
}

const isSame = (a: TWatched, b: TWatched) =>
  a.project === b.project &&
  a.selected === b.selected &&
  a.secondarySelected === b.secondarySelected &&
  a.isCompiled === b.isCompiled &&
  a.isSecondaryCompiled === b.isSecondaryCompiled &&
  a.contentWindows === b.contentWindows;

/**
 * Пишет срез layout'а в хранилище при изменении наблюдаемых полей.
 *
 * Подписка, а не сага на список экшенов: перечень экшенов, меняющих layout, пришлось
 * бы поддерживать руками, и он молча разъехался бы при добавлении нового UI-состояния.
 * Подписка ловит изменение состояния, а не его причину.
 */
export function subscribeLayoutPersistence(store: TLayoutStore): () => void {
  let previous = watched(store.getState());

  // Снапшот берётся в момент ИЗМЕНЕНИЯ, а не в момент записи. Иначе сценарий
  // «передвинул окно → через 200 мс вышел в список проектов» терял бы перемещение:
  // к моменту срабатывания таймера проект уже null.
  let pending: { projectId: string; layout: TEditorLayout } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const write = () => {
    if (!pending) {
      return;
    }

    const { projectId, layout } = pending;
    pending = null;
    saveLayout(projectId, layout);
  };

  const schedule = () => {
    if (timer !== null) {
      return;
    }

    timer = setTimeout(() => {
      timer = null;
      write();
    }, WRITE_THROTTLE_MS);
  };

  const flush = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }

    write();
  };

  const listener = () => {
    const state = store.getState();
    const next = watched(state);

    if (isSame(previous, next)) {
      return;
    }

    previous = next;

    const { project, selected } = state.editor;

    // Уходим из проекта — дописываем отложенное немедленно. Иначе «подвинул окно →
    // в пределах 500 мс вышел в список → сразу вернулся» терял бы перемещение:
    // restore успевал прочитать ещё не обновлённый блоб. Заодно очередь не может
    // воскресить срез проекта, удалённого сразу после выхода.
    if (!project) {
      flush();
      return;
    }

    // Без выбранной заметки срез бессмысленен. Плюс это защита от записи
    // `selected: null` сразу после LAYOUT_RESTORE, когда роутинг ещё не отработал.
    if (!selected) {
      return;
    }

    // Гейт: между PROJECT_SET и LAYOUT_RESTORE в сторе лежат окна прошлого проекта.
    if (getRestoredProjectId() !== project.id) {
      return;
    }

    // Гейт открывается раньше, чем роутинг успевает выставить selected, поэтому при
    // переходе между проектами по прямому URL здесь на мгновение лежит заметка
    // ПРЕДЫДУЩЕГО проекта. Записав её, мы бы сломали вход в новый проект.
    if (!project.notes?.[selected.id] && !project.collections?.[selected.id]) {
      return;
    }

    const layout = collectLayout(state);

    if (!layout) {
      return;
    }

    // Отложенный снапшот принадлежит другому проекту — дописываем его сейчас,
    // иначе он молча потеряется под новым.
    if (pending && pending.projectId !== project.id) {
      flush();
    }

    pending = { projectId: project.id, layout };
    schedule();
  };

  const unsubscribeStore = store.subscribe(listener);

  // Без этого F5 в пределах окна throttle теряет последнее действие — ровно в фиче,
  // смысл которой «вернуться туда же после перезагрузки». visibilitychange нужен
  // дополнительно к pagehide из-за мобильного Safari.
  const handlePageHide = () => flush();
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  };

  window.addEventListener('pagehide', handlePageHide);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    unsubscribeStore();
    window.removeEventListener('pagehide', handlePageHide);
    document.removeEventListener('visibilitychange', handleVisibilityChange);

    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }

    pending = null;
  };
}
